/**
 * /ai/skills — skill cards: model, draft badge, goldens count, last eval
 * accuracy, fingerprint presence. Pseudo-skills (_classifier, _generic) are
 * shown separately — they're pipeline machinery, not extraction skills.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useRouteMatch } from 'react-router-dom';
import {
  Box,
  Heading,
  HStack,
  SimpleGrid,
  Text,
  Button,
  Badge,
  Spinner,
  Center,
  Tag,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FiPlus, FiRefreshCw, FiCpu } from 'react-icons/fi';
import { callDocai } from '../../hooks/useDocAI';

const fmtPct = (x) => (x == null ? '—' : `${(x * 100).toFixed(2)}%`);

export default function AISkillsPage() {
  const [skills, setSkills] = useState(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const { url } = useRouteMatch();
  const sectionUrl = url.replace(/\/skills$/, '');

  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await callDocai('docaiSkills', { action: 'list' });
      setSkills(res.skills || []);
    } catch (err) {
      toast({ title: 'Skills load failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !skills) {
    return (
      <Center h="60vh"><Spinner size="xl" color="blue.400" /></Center>
    );
  }

  const real = (skills || []).filter((s) => !s.name.startsWith('_'));
  const pseudo = (skills || []).filter((s) => s.name.startsWith('_'));

  const card = (s) => (
    <Box
      key={s.name}
      as={Link}
      to={`${sectionUrl}/skills/${s.name}`}
      bg={cardBg}
      borderWidth="1px"
      borderColor={cardBorder}
      borderRadius="lg"
      p={4}
      _hover={{ borderColor: 'blue.400', textDecoration: 'none' }}
    >
      <HStack justify="space-between" mb={2}>
        <HStack>
          <FiCpu />
          <Text fontWeight="semibold" fontSize="sm" fontFamily="mono">{s.name}</Text>
        </HStack>
        <HStack>
          {s.is_draft && <Badge colorScheme="orange">draft</Badge>}
          <Badge variant="outline">v{s.version}</Badge>
        </HStack>
      </HStack>
      <Text fontSize="xs" color="gray.500" mb={3}>
        {s.doc_type || 'no doc type'}{s.lender ? ` · ${s.lender}` : ''}
      </Text>
      <HStack spacing={2} flexWrap="wrap">
        <Tag size="sm">{s.model || 'no model'}</Tag>
        <Tag size="sm" colorScheme="purple">{s.goldens} golden{s.goldens === 1 ? '' : 's'}</Tag>
        <Tag size="sm" colorScheme={s.lastEval ? 'green' : 'gray'}>
          eval {s.lastEval ? fmtPct(s.lastEval.totals?.fieldAccuracy) : 'never'}
        </Tag>
        <Tag size="sm" colorScheme={s.hasFingerprint ? 'blue' : 'gray'}>
          {s.hasFingerprint ? 'fingerprint ✓' : 'no fingerprint'}
        </Tag>
      </HStack>
    </Box>
  );

  return (
    <Box p={6}>
      <HStack justify="space-between" mb={5}>
        <Heading size="md">Skills</Heading>
        <HStack>
          <Button size="sm" leftIcon={<FiRefreshCw />} variant="outline" onClick={load} isLoading={loading}>
            Refresh
          </Button>
          <Button as={Link} to={`${sectionUrl}/skills/new`} size="sm" colorScheme="blue" leftIcon={<FiPlus />}>
            Bootstrap new skill
          </Button>
        </HStack>
      </HStack>

      <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4} mb={8}>
        {real.map(card)}
      </SimpleGrid>

      <Heading size="sm" mb={3} color="gray.500">Pipeline pseudo-skills</Heading>
      <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
        {pseudo.map(card)}
      </SimpleGrid>
    </Box>
  );
}
